import { ChunkData, ChunkedMapData, SceneId, ServerMessagePayload, TilesetData } from '@common/api';
import { getChunkKey } from '@common/chunkUtils';
import { Assets, Container, Rectangle, Sprite, Texture } from 'pixi.js';

import { AssetManager } from '../AssetManager';
import { ClientChunkDataService } from '../services/ClientChunkDataService';
import { ClientServiceContainer } from '../services/ClientServiceContainer';
import { ClientGlobalSystem } from './ClientBase';

/**
 * Chunk-based map rendering system.
 *
 * Renders map tiles incrementally as chunks are loaded/unloaded via AOI.
 * Hierarchy: [Ground/Above] -> Global Layer Containers -> Chunk-specific Layer Pieces.
 * - Listens for ChunkLoaded/ChunkUnloaded events
 * - Loads tileset assets once when scene metadata arrives
 * - Maintains deterministic Z-ordering of chunks within each layer for correct overlap
 */
export class MapRenderSystem extends ClientGlobalSystem {
  private groundContainer: Container;
  private aboveContainer: Container;

  /** Currently rendered scene */
  private currentSceneId: SceneId | undefined;

  /** Global layer containers: layerName -> Container */
  private layerContainers: Map<string, Container> = new Map();

  /** Individual chunk pieces per layer: chunkKey -> Container[] (one piece per layer) */
  private chunkLayerPieces: Map<string, Container[]> = new Map();

  /** Loaded tileset textures: tilesetName -> Texture */
  private tilesetTextures: Map<string, Texture> = new Map();

  /** Per-tile textures for collection tilesets: `${tilesetName}_${localId}` -> Texture */
  private tileTextures: Map<string, Texture> = new Map();

  /** Are assets currently loading? */
  private loadingAssets = false;

  /** Have assets been loaded for current scene? */
  private assetsLoaded = false;

  /** Chunks waiting to be rendered (received before assets loaded) */
  private pendingChunks: Map<string, { sceneId: SceneId; chunkX: number; chunkY: number }> =
    new Map();

  constructor(clientServices: ClientServiceContainer) {
    super(clientServices);

    this.groundContainer = new Container();
    this.groundContainer.label = 'GroundMapContainer';
    this.groundContainer.zIndex = -1000000;

    this.aboveContainer = new Container();
    this.aboveContainer.label = 'AboveMapContainer';
    this.aboveContainer.zIndex = 1000000;
  }

  run(): ServerMessagePayload[] {
    const world = this.getWorld();
    const eventBus = this.clientServices.common.eventBus;
    const chunkDataService = this.clientServices.chunkData;
    const renderService = this.clientServices.render;
    const stage = renderService.stage;

    if (!stage) {
      console.warn('[MapRenderSystem] No stage!');
      return [];
    }

    // Ensure map containers are on stage
    if (!stage.children.includes(this.groundContainer)) {
      stage.addChild(this.groundContainer);
    }
    if (!stage.children.includes(this.aboveContainer)) {
      stage.addChild(this.aboveContainer);
    }
    stage.sortableChildren = true;

    // Get current scene - prefer from player transform, fallback to sceneMetadata
    let sceneId: SceneId | undefined;

    const playerId = this.clientServices.localPlayer.entityId;
    if (playerId !== undefined) {
      const transform = world.getComponent(playerId, 'Transform');
      if (transform?.sceneId) {
        sceneId = transform.sceneId;
      }
    }

    // Fallback: get first scene from sceneMetadata if player entity doesn't exist yet
    if (!sceneId) {
      const allScenes = chunkDataService.getAllSceneIds();
      if (allScenes.length > 0) {
        sceneId = allScenes[0];
      }
    }

    if (!sceneId) {
      return []; // Can't do anything without knowing the scene
    }

    // Scene changed - reset everything
    if (this.currentSceneId !== sceneId) {
      this.handleSceneChange(sceneId);
    }

    if (!this.assetsLoaded && !this.loadingAssets) {
      const metadata = chunkDataService.getSceneMetadata(sceneId);
      if (metadata) {
        this.loadAssets(metadata, sceneId, chunkDataService);
      }
    }

    // Process chunk events
    const chunkEvents = eventBus.getEventsOfType('ChunkLoaded');
    for (const event of chunkEvents) {
      if (event.payload.sceneId === sceneId) {
        this.handleChunkLoaded(
          event.payload.sceneId,
          event.payload.chunkX,
          event.payload.chunkY,
          chunkDataService,
        );
      }
    }

    for (const event of eventBus.getEventsOfType('ChunkUnloaded')) {
      if (event.payload.sceneId === sceneId) {
        this.handleChunkUnloaded(event.payload.chunkX, event.payload.chunkY);
      }
    }

    // Check for MapReloadRequested event
    for (const event of eventBus.getEventsOfType('MapReloadRequested')) {
      if (event.payload.sceneId === sceneId) {
        this.reload();
      }
    }

    return [];
  }

  /**
   * Handle scene change - clear all rendered chunks.
   */
  private handleSceneChange(newSceneId: SceneId): void {
    this.currentSceneId = newSceneId;

    // Clear all layer containers
    for (const container of this.layerContainers.values()) {
      container.removeFromParent();
      container.destroy({ children: true });
    }
    this.layerContainers.clear();
    this.chunkLayerPieces.clear();

    // Reset asset loading state
    this.tilesetTextures.clear();
    this.tileTextures.clear();
    this.assetsLoaded = false;
    this.loadingAssets = false;
    this.pendingChunks.clear();
  }

  /**
   * Load tileset assets for the current scene.
   */
  private async loadAssets(
    metadata: ChunkedMapData,
    _sceneId: SceneId,
    chunkDataService: ClientChunkDataService,
  ): Promise<void> {
    if (this.loadingAssets) return;
    this.loadingAssets = true;

    let allSucceeded = true;

    try {
      const loadPromises = metadata.tilesets.map((tileset) =>
        this.loadTilesetAssets(tileset).then((success) => {
          if (!success) allSucceeded = false;
        }),
      );

      await Promise.all(loadPromises);

      // Only mark assets as loaded if all tilesets succeeded
      this.assetsLoaded = allSucceeded;

      if (!allSucceeded) {
        console.warn('[MapRenderSystem] Some tilesets failed to load, rendering may be incomplete');
      }

      // Render any pending chunks first
      if (this.pendingChunks.size > 0) {
        for (const [, pending] of this.pendingChunks) {
          this.renderChunkFromState(
            pending.sceneId,
            pending.chunkX,
            pending.chunkY,
            chunkDataService,
          );
        }
        this.pendingChunks.clear();
      }

      // Also render any chunks already in chunks for this scene
      // (may have been loaded before scene change was detected)
      if (this.currentSceneId) {
        const sceneChunks = chunkDataService.getChunks(this.currentSceneId);
        if (sceneChunks) {
          for (const [chunkKey] of sceneChunks) {
            if (!this.chunkLayerPieces.has(chunkKey)) {
              const [chunkX, chunkY] = chunkKey.split(',').map(Number);
              this.renderChunkFromState(this.currentSceneId, chunkX, chunkY, chunkDataService);
            }
          }
        }
      }
    } finally {
      // Always reset loading flag so retries are possible
      this.loadingAssets = false;
    }
  }

  /**
   * Load assets for a single tileset.
   * Returns true if all assets in the tileset loaded successfully.
   */
  private async loadTilesetAssets(tileset: TilesetData): Promise<boolean> {
    try {
      // Check if this is a collection tileset with per-tile images
      if (tileset.tileImages && Object.keys(tileset.tileImages).length > 0) {
        let allTilesLoaded = true;
        // Load each tile's individual image
        const tileLoadPromises = Object.entries(tileset.tileImages).map(
          async ([localIdStr, tileImage]) => {
            try {
              const texture = await Assets.load(tileImage.source);
              texture.source.scaleMode = 'nearest';
              const key = `${tileset.name}_${localIdStr}`;
              this.tileTextures.set(key, texture);

              // Register with AssetManager for entity sprites
              // Extract texture ID from source path: /assets/rock.png -> rock
              const filename = tileImage.source.split('/').pop() ?? '';
              const textureId = filename.replace(/\.[^.]+$/, '').replace(/\?.*$/, '');
              if (textureId) {
                AssetManager.getInstance().registerTexture(textureId, texture);
              }
            } catch (e) {
              console.error(`[MapRenderSystem] Failed to load tile image: ${tileImage.source}`, e);
              allTilesLoaded = false;
            }
          },
        );
        await Promise.all(tileLoadPromises);
        return allTilesLoaded;
      } else if (tileset.source) {
        // Single spritesheet image
        const texture = await Assets.load(tileset.source);
        this.tilesetTextures.set(tileset.name, texture);

        // Register each tile as a texture in the AssetManager so ECS entities (Map Objects)
        // can resolve them via their textureId.
        this.registerTilesetTextures(tileset, texture);

        return true;
      }
      return true;
    } catch (e) {
      console.error(`[MapRenderSystem] Failed to load tileset: ${tileset.source}`, e);
      return false;
    }
  }

  /**
   * Slice a tileset spritesheet into individual tile textures and register them with AssetManager.
   * Required for client-side rendering of ECS entities spawned from Tiled map objects.
   */
  private registerTilesetTextures(tileset: TilesetData, sourceTexture: Texture): void {
    const cols = tileset.columns || 1;
    const count = tileset.tilecount || 0;

    for (let i = 0; i < count; i++) {
      const tx = (i % cols) * tileset.tilewidth;
      const ty = Math.floor(i / cols) * tileset.tileheight;

      const tileTexture = new Texture({
        source: sourceTexture.source,
        frame: new Rectangle(tx, ty, tileset.tilewidth, tileset.tileheight),
      });
      tileTexture.source.scaleMode = 'nearest';

      // Register with AssetManager using the same format as MapObjectService on the server.
      // This is the key the client renderer uses to resolve the sprite's texture.
      const key = `${tileset.name}_${i}`;
      AssetManager.getInstance().registerTexture(key, tileTexture);
    }
  }

  /**
   * Render a chunk from state data (used for pending chunks).
   */
  private renderChunkFromState(
    sceneId: SceneId,
    chunkX: number,
    chunkY: number,
    chunkDataService: ClientChunkDataService,
  ): void {
    const chunkKey = getChunkKey(chunkX, chunkY);

    // Already rendered?
    if (this.chunkLayerPieces.has(chunkKey)) {
      return;
    }

    // Get chunk data
    const chunkData = chunkDataService.getChunks(sceneId)?.get(chunkKey);
    if (!chunkData) {
      console.warn(`[MapRenderSystem] No chunk data for pending ${chunkKey}`);
      return;
    }

    // Get metadata for tileset info
    const metadata = chunkDataService.getSceneMetadata(sceneId);
    if (!metadata) {
      console.warn(`[MapRenderSystem] No metadata for scene ${sceneId}`);
      return;
    }

    this.renderChunk(chunkX, chunkY, chunkData, metadata);
  }

  /**
   * Handle a chunk being loaded - render its tiles.
   */
  private handleChunkLoaded(
    sceneId: SceneId,
    chunkX: number,
    chunkY: number,
    chunkDataService: ClientChunkDataService,
  ): void {
    const chunkKey = getChunkKey(chunkX, chunkY);

    // Already rendered?
    if (this.chunkLayerPieces.has(chunkKey)) {
      return;
    }

    // Assets not loaded yet? Queue for later
    if (!this.assetsLoaded) {
      this.pendingChunks.set(chunkKey, { sceneId, chunkX, chunkY });
      return;
    }

    // Get chunk data
    const chunkData = chunkDataService.getChunks(sceneId)?.get(chunkKey);
    if (!chunkData) {
      console.warn(`[MapRenderSystem] No chunk data for ${chunkKey}`);
      return;
    }

    // Get metadata for tileset info
    const metadata = chunkDataService.getSceneMetadata(sceneId);
    if (!metadata) {
      console.warn(`[MapRenderSystem] No metadata for scene ${sceneId}`);
      return;
    }

    this.renderChunk(chunkX, chunkY, chunkData, metadata);
  }

  /**
   * Handle a chunk being unloaded - remove its sprites.
   */
  private handleChunkUnloaded(chunkX: number, chunkY: number): void {
    const chunkKey = getChunkKey(chunkX, chunkY);
    const pieces = this.chunkLayerPieces.get(chunkKey);

    if (pieces) {
      for (const piece of pieces) {
        piece.removeFromParent();
        piece.destroy({ children: true });
      }
      this.chunkLayerPieces.delete(chunkKey);
    }
  }

  private renderChunk(
    chunkX: number,
    chunkY: number,
    chunkData: ChunkData,
    metadata: ChunkedMapData,
  ): void {
    const chunkKey = getChunkKey(chunkX, chunkY);
    const chunkSize = metadata.chunkSize;
    const tileWidth = metadata.tileWidth;
    const tileHeight = metadata.tileHeight;

    const chunkPieces: Container[] = [];

    // Calculate world position
    const worldX = chunkX * chunkSize * tileWidth;
    const worldY = chunkY * chunkSize * tileHeight;

    // Use a deterministic zIndex for chunk pieces to ensure consistent overlap.
    // Tiled renders tiles left-to-right within a row, and rows from top (smaller Y)
    // to bottom (larger Y). To match this, chunks/tiles with larger world Y (visually
    // lower on screen) should be drawn on top of those with smaller Y, and for the
    // same Y, larger X should be drawn on top of smaller X. The large multiplier for Y
    // encodes this row-major order in the zIndex.
    const COORD_OFFSET = 1_000_000;
    const X_RANGE = 2 * COORD_OFFSET + 1;
    const shiftedY = chunkY + COORD_OFFSET;
    const shiftedX = chunkX + COORD_OFFSET;
    const chunkZIndex = shiftedY * X_RANGE + shiftedX;

    // Render each layer
    for (const [layerName, tiles] of Object.entries(chunkData.layers)) {
      const group = metadata.layerGroups?.[layerName];

      // CRITICAL: Only render layers in explicitly defined "Ground" or "Above" groups per user requirement.
      if (group !== 'Ground' && group !== 'Above') {
        continue;
      }

      // Get or create global layer container
      let layerContainer = this.layerContainers.get(layerName);
      if (!layerContainer) {
        layerContainer = new Container();
        layerContainer.label = `LayerContainer_${layerName}`;
        layerContainer.sortableChildren = true;
        this.layerContainers.set(layerName, layerContainer);

        if (group === 'Ground') {
          this.groundContainer.addChild(layerContainer);
        } else {
          this.aboveContainer.addChild(layerContainer);
        }
      }

      // Create a container for this chunk's piece of this layer
      const layerChunkPiece = new Container();
      layerChunkPiece.label = `ChunkPiece_${layerName}_${chunkKey}`;
      layerChunkPiece.x = worldX;
      layerChunkPiece.y = worldY;
      layerChunkPiece.zIndex = chunkZIndex;

      for (let localY = 0; localY < chunkSize; localY++) {
        for (let localX = 0; localX < chunkSize; localX++) {
          const index = localY * chunkSize + localX;
          const gid = tiles[index];

          if (gid === 0) continue;

          const sprite = this.createTileSprite(gid, metadata.tilesets);
          if (sprite) {
            sprite.x = localX * tileWidth;
            // Tiled uses bottom-left origin for tiles.
            // Align bottom of sprite with bottom of grid cell.
            sprite.y = (localY + 1) * tileHeight - sprite.height;
            layerChunkPiece.addChild(sprite);
          }
        }
      }

      if (layerChunkPiece.children.length > 0) {
        layerContainer.addChild(layerChunkPiece);
        chunkPieces.push(layerChunkPiece);
      } else {
        layerChunkPiece.destroy();
      }
    }

    this.chunkLayerPieces.set(chunkKey, chunkPieces);
  }

  /**
   * Create a sprite for a tile GID.
   */
  private createTileSprite(gid: number, tilesets: TilesetData[]): Sprite | null {
    // Find the tileset that contains this GID
    const tileset = tilesets
      .filter((ts) => ts.firstgid <= gid)
      .sort((a, b) => b.firstgid - a.firstgid)[0];

    if (!tileset) return null;

    const localId = gid - tileset.firstgid;

    // Check for per-tile texture first (collection tilesets)
    const tileTextureKey = `${tileset.name}_${localId}`;
    const perTileTexture = this.tileTextures.get(tileTextureKey);
    if (perTileTexture) {
      return new Sprite(perTileTexture);
    }

    // Fall back to spritesheet logic
    const texture = this.tilesetTextures.get(tileset.name);
    if (!texture) return null;

    const cols = tileset.columns || 1;
    const tx = (localId % cols) * tileset.tilewidth;
    const ty = Math.floor(localId / cols) * tileset.tileheight;

    const tileTexture = new Texture({
      source: texture.source,
      frame: new Rectangle(tx, ty, tileset.tilewidth, tileset.tileheight),
    });
    tileTexture.source.scaleMode = 'nearest';

    return new Sprite(tileTexture);
  }

  /**
   * Force reload - clears and re-renders visible chunks.
   */
  public reload(): void {
    // Clear all layer containers
    for (const container of this.layerContainers.values()) {
      container.removeFromParent();
      container.destroy({ children: true });
    }
    this.layerContainers.clear();
    this.chunkLayerPieces.clear();

    // Reset for new scene
    this.currentSceneId = undefined;
    this.assetsLoaded = false;
    this.tilesetTextures.clear();
    this.tileTextures.clear();
  }

  /**
   * Full reset for reconnection - clears all state including mapContainer.
   * Preserves parent reference to maintain render hierarchy.
   */
  public reset(): void {
    this.reload();
    this.groundContainer.removeFromParent();
    this.groundContainer.destroy({ children: true });
    this.aboveContainer.removeFromParent();
    this.aboveContainer.destroy({ children: true });

    this.groundContainer = new Container();
    this.groundContainer.label = 'GroundMapContainer';
    this.groundContainer.zIndex = -1000000;

    this.aboveContainer = new Container();
    this.aboveContainer.label = 'AboveMapContainer';
    this.aboveContainer.zIndex = 1000000;

    const renderService = this.clientServices.render;
    const stage = renderService.stage;
    if (stage) {
      stage.addChild(this.groundContainer);
      stage.addChild(this.aboveContainer);
    }

    this.pendingChunks.clear();
    this.loadingAssets = false;
  }
}
